import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Button,
    Menu,
    MenuItem,
    Grid,
    FormControl,
    InputLabel,
    Select,
    Box,
    CircularProgress,
    Typography,
    Tooltip,
    styled,
} from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import InfoIcon from '@mui/icons-material/Info';
import { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import b64toBlob from 'b64-to-blob';
import '../Widgets.css';

import { useChpContext } from '../../../hooks/chpContext';
import { fetchRevenueMetrics, fetchRevenueMetricsComparisonCount, exportRevenueMetrics, MenuProps } from '../../Widgets/WidgetService';
import RevenueMetrics from '../../../Models/Widget/RevenueMetrics.model';
import { MemberStatusEnum } from '../../../Enum/MemberStatusEnum';
import { DateContextTypeEnum } from '../../../Enum/DateContextTypeEnum';
import HealthPlanMultiSelect from '../../Shared/HealthPlanMultiSelect';

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} classes={{ popper: className }} />)({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: 800,
    },
});

const RevenueMetricsSummary = () => {
    const priorMonth = new Date();
    priorMonth.setDate(0);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const { cities, getCities } = useChpContext();
    const [selectedPlans, setSelectedPlans] = useState<string[]>(['All']);
    const [selectedCity, setSelectedCity] = useState<string>('All');
    const [revenueMetrics, setRevenueMetrics] = useState<RevenueMetrics>();
    const [activeComparisonCount, setActiveComparisonCount] = useState<number>(0);
    const [lostInsuranceCoverageComparisonCount, setLostInsuranceCoverageComparisonCount] = useState<number>(0);
    const [preparingForGraduationComparisonCount, setPreparingForGraduationComparisonCount] = useState<number>(0);
    const [scheduledComparisonCount, setScheduledComparisonCount] = useState<number>(0);
    const [incarceratedComparisonCount, setIncarceratedComparisonCount] = useState<number>(0);
    const [isDataShow, setIsDataShow] = useState<boolean>(false);
    const [isActiveDataShow, setIsActiveDataShow] = useState<boolean>(false);
    const [isLostInsuranceCoverageDataShow, setIsLostInsuranceCoverageDataShow] = useState<boolean>(false);
    const [isPreparingForGraduationDataShow, setIsPreparingForGraduationDataShow] = useState<boolean>(false);
    const [isSchedulingDataShow, setIsSchedulingDataShow] = useState<boolean>(false);
    const [isIncarceratedDataShow, setIsIncarceratedDataShow] = useState<boolean>(false);
    const [fromDate, setFromDate] = useState(new Date());
    const [toDate, setToDate] = useState(priorMonth);
    const [show, setShow] = useState(false);
    const ref = useRef(null);
    const [downLoading, setDownLoading] = useState<boolean>(false);

    const handleClick = (event) => {
        setShow(!show);
    };

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        getCities();

        setIsFetched(true);
    }, [isFetched, setIsFetched, getCities]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    const fetchComparisonCount = async (status: MemberStatusEnum, setter: (value: number) => void, setShow: (value: boolean) => void, requestId: string, end: string | null) => {
        try {
            const response = await fetchRevenueMetricsComparisonCount(selectedPlans.join(","), selectedCity, end, status);
            if (requestId === (window as any).renewRequestId) {
                setter(response);
            }
        } catch (error) {
            console.error(`Error fetching comparison count for ${status}:`, error);
        } finally {
            setShow(true);
        }
    };

    useEffect(() => {
        const newRequestId = String(Math.random());
        (window as any).renewRequestId = newRequestId;

        const changeFilter = async () => {
            setIsDataShow(false);
            setIsActiveDataShow(false);
            setIsSchedulingDataShow(false);
            setIsLostInsuranceCoverageDataShow(false);
            setIsPreparingForGraduationDataShow(false);

            let start = fromDate ? new Date(fromDate).toDateString() : null;
            let end = toDate ? new Date(toDate).toDateString() : null;

            try {
                const revenueResponse = await fetchRevenueMetrics(selectedPlans.join(","), selectedCity, start, end);
                setRevenueMetrics(revenueResponse);
            } catch (error) {
                console.error('Error fetching revenue metrics:', error);
            } finally {
                setIsDataShow(true);
            }

            await Promise.all([
                fetchComparisonCount(MemberStatusEnum.Active, setActiveComparisonCount, setIsActiveDataShow, newRequestId, end),
                fetchComparisonCount(MemberStatusEnum.Scheduled, setScheduledComparisonCount, setIsSchedulingDataShow, newRequestId, end),
                fetchComparisonCount(MemberStatusEnum.LostInsuranceCoverage, setLostInsuranceCoverageComparisonCount, setIsLostInsuranceCoverageDataShow, newRequestId, end),
                fetchComparisonCount(MemberStatusEnum.PreparingForGraduation, setPreparingForGraduationComparisonCount, setIsPreparingForGraduationDataShow, newRequestId, end),
                fetchComparisonCount(MemberStatusEnum.Incarcerated, setIncarceratedComparisonCount, setIsIncarceratedDataShow, newRequestId, end),
            ]);
        };

        changeFilter();
    }, [selectedPlans, selectedCity, fromDate, toDate]);

    const exportRevenueVolumeMetric = async (dateContextType: DateContextTypeEnum) => {
        handleClose();
        setDownLoading(true);
        let start = fromDate ? new Date(fromDate).toDateString() : null;
        let end = toDate ? new Date(toDate).toDateString() : null;
        try {
            const data = await exportRevenueMetrics(selectedPlans.join(","), selectedCity, start, end, dateContextType);
            let blob = b64toBlob(data.base64String, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
            let url = window.URL.createObjectURL(blob);
            let link = document.createElement('a');
            link.href = url;
            const date = new Date();
            const dateInfo = date.toISOString().split('T')[0].replace(/-/g, '');
            link.setAttribute('download', `RevenueVolumeMetrics - ${dateInfo}.xlsx`);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error exporting revenue metrics:', error);
        } finally {
            setDownLoading(false);
        }
    };

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <Grid size={{ xs: 12, lg: 6 }}>
            <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                    <Grid size={12} textAlign="center">
                        <Typography display={'inline-block'} variant="h6" marginRight={1} color={'primary'} ref={ref}>
                            Revenue / Volume Metrics
                            <CustomWidthTooltip
                                open={show}
                                title={
                                    <div>
                                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                            Active Today :
                                        </Typography>
                                        <Typography variant="caption">Number of “Active” members today</Typography>
                                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                            Active Comparison :
                                        </Typography>
                                        <Typography variant="caption">Number of “Active” members on compared date</Typography>
                                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                            Lost Insurance Coverage Today :
                                        </Typography>
                                        <Typography variant="caption">Number of “Lost Insurance Coverage” members today</Typography>
                                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                            Lost Insurance Coverage Comparison :
                                        </Typography>
                                        <Typography variant="caption">Number of “Lost Insurance Coverage” members on compared date</Typography>
                                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                            Preparing For Graduation Today :
                                        </Typography>
                                        <Typography variant="caption">Number of “Preparing For Graduation” members today</Typography>
                                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                            Preparing For Graduation Comparison :
                                        </Typography>
                                        <Typography variant="caption">Number of “Preparing For Graduation” members on compared date</Typography>
                                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                            Scheduled Today :
                                        </Typography>
                                        <Typography variant="caption">Number of “Scheduled” members today</Typography>
                                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                            Scheduled Comparison :
                                        </Typography>
                                        <Typography variant="caption">Number of “Scheduled” members on compared date</Typography>
                                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                            Newly Onboarded Today :
                                        </Typography>
                                        <Typography variant="caption">Number of “Newly Onboarded” members from the start of the month to the today date</Typography>
                                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                            Newly Onboarded Comparison :
                                        </Typography>
                                        <Typography variant="caption">Number of “Newly Onboarded” members from the start of the month to the selected date</Typography>
                                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                            Refused to Continue Service Today :
                                        </Typography>
                                        <Typography variant="caption">Number of “Refused To Continue Services” members from the start of the month to the today date</Typography>
                                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                            Refused to Continue Service Comparison :
                                        </Typography>
                                        <Typography variant="caption">Number of “Refused To Continue Services” members from the start of the month to the selected date</Typography>
                                    </div>
                                }
                                PopperProps={{ style: { zIndex: 1000 } }}
                            >
                                <IconButton onClick={handleClick}>
                                    <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                </IconButton>
                            </CustomWidthTooltip>
                        </Typography>
                    </Grid>
                    <Grid size={4}>
                        <HealthPlanMultiSelect
                            value={selectedPlans}
                            onChange={setSelectedPlans}
                            menuProps={MenuProps}
                        />
                    </Grid>
                    <Grid size={4}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Geography</InputLabel>
                            <Select
                                value={selectedCity}
                                label="Geography"
                                name="Geography"
                                onChange={(e) => setSelectedCity(e.target.value)}
                                MenuProps={MenuProps}
                            >
                                <MenuItem key="All" value="All">
                                    All
                                </MenuItem>
                                {cities.map((city) => (
                                    <MenuItem key={city} value={city}>
                                        {city}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Grid size={{ md: 4 }}>
                            <DesktopDatePicker
                                disabled={true}
                                label="Default Date"
                                value={fromDate || null}
                                minDate={new Date('2017-01-01')}
                                onChange={(newValue) => setFromDate(newValue)}
                                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                            />
                        </Grid>
                        <Grid size={{ md: 4 }}>
                            <DesktopDatePicker
                                label="Comparison Date"
                                value={toDate || null}
                                minDate={new Date('2017-01-01')}
                                onChange={(newValue) => setToDate(newValue)}
                                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                            />
                        </Grid>
                    </LocalizationProvider>
                    <Grid size={5.6}></Grid>
                    <Grid size={2} display={'flex'} maxHeight="55px">
                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleOpen}
                            style={{ minWidth: '120px' }}
                            disabled={
                                downLoading ||
                                !isDataShow ||
                                !isActiveDataShow ||
                                !isSchedulingDataShow ||
                                !isLostInsuranceCoverageDataShow ||
                                !isPreparingForGraduationDataShow||
                                !isIncarceratedDataShow
                            }
                        >
                            {downLoading ? 'Exporting...' : 'Export'}
                        </Button>
                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                        >
                            <MenuItem onClick={() => exportRevenueVolumeMetric(DateContextTypeEnum.TodayDate)}>Today Date</MenuItem>
                            <MenuItem onClick={() => exportRevenueVolumeMetric(DateContextTypeEnum.ComparisonDate)}>Comparison Date</MenuItem>
                        </Menu>
                    </Grid>
                    <Grid size={12} marginTop={1} sx={{ position: 'relative', height: 'calc(100vh - 200px)', maxHeight: '400px' }}>
                        {!isDataShow ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : (
                            <TableContainer component={Paper}>
                                <Table aria-label="simple table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Members</TableCell>
                                            <TableCell align="center">Today</TableCell>
                                            <TableCell align="center">Comparison Date</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>Active</TableCell>
                                            <TableCell align="center">{revenueMetrics.ActiveMembersCount}</TableCell>
                                            <TableCell align="center">
                                                {!isActiveDataShow ? <CircularProgress size={15} /> : activeComparisonCount}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Lost Insurance Coverage</TableCell>
                                            <TableCell align="center">{revenueMetrics.LostInsuranceCoverageMembersCount}</TableCell>
                                            <TableCell align="center">
                                                {!isLostInsuranceCoverageDataShow ? (
                                                    <CircularProgress size={15} />
                                                ) : (
                                                    lostInsuranceCoverageComparisonCount
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Preparing For Graduation</TableCell>
                                            <TableCell align="center">{revenueMetrics.PreparingForGraduationMembersCount}</TableCell>
                                            <TableCell align="center">
                                                {!isPreparingForGraduationDataShow ? (
                                                    <CircularProgress size={15} />
                                                ) : (
                                                    preparingForGraduationComparisonCount
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Onboarding Scheduled</TableCell>
                                            <TableCell align="center">{revenueMetrics.OnboardScheduledCount}</TableCell>
                                            <TableCell align="center">
                                                {!isSchedulingDataShow ? <CircularProgress size={15} /> : scheduledComparisonCount}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Newly Onboarded</TableCell>
                                            <TableCell align="center">{revenueMetrics.OnboardedMembersCount}</TableCell>
                                            <TableCell align="center">{revenueMetrics.ComparedOnboardedMembersCount}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Incarcerated</TableCell>
                                            <TableCell align="center">{revenueMetrics.IncarceratedMembersCount}</TableCell>
                                            <TableCell align="center">
                                                {!isIncarceratedDataShow ? (
                                                    <CircularProgress size={15} />
                                                ) : (
                                                    incarceratedComparisonCount
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Refused to Continue Services</TableCell>
                                            <TableCell align="center">{revenueMetrics.RefusedToContinueServicesMembersCount}</TableCell>
                                            <TableCell align="center">{revenueMetrics.ComparedRefusedToContinueServiceMembersCount}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );
};

export default RevenueMetricsSummary;
